import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ExclamationCircleIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { getRooms } from "../../slices/rooms";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import {
  createAnnouncementNow,
  createAnnouncementRecurringInterval,
  createAnnouncementRecurringPeriod,
  createAnnouncementScheduled,
  deleteAnnouncements,
  getAnnouncements,
  updatePeriodicAnnouncementStatus,
} from "../../slices/communication";
import { useSearchParams } from "react-router-dom";
import Dropdown from "../forms/Dropdown";
import HorizontalRadio from "../forms/HorizontalRadio";
import IntegerInput from "../forms/IntegerInput";
import { daysOfMonth, daysOfWeek } from "../../utils/calendar";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import DatetimeSelector from "../forms/DatetimeSelector";
import TimeSelector from "../forms/TimeSelector";
import { formatDatetimeAsString } from "../../utils/datetime";
import Filters from "../layout/Filters";
import Table from "../layout/Table";
import Badge from "../layout/Badge";
import { getColour } from "../../utils/colours";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";

export default function Announcements() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const pageNum =
    searchParams.get("page") === null
      ? 1
      : parseInt(searchParams.get("page") as string);
  const searchType = searchParams.get("type");

  // Fetch data from server
  useEffect(() => {
    dispatch(getAnnouncements({ pageNum, type: searchType }));
    dispatch(getRooms());
  }, [dispatch, pageNum, searchType]);

  // Set state
  const [type, setType] = useState({ value: "" });
  const [selectedAnnouncements, setSelectedAnnouncements] = useState<string[]>(
    [],
  );
  const [createAnnouncementForm, setCreateAnnouncementForm] = useState<{
    text: { [key: string]: string };
    rooms: string[];
    type: "A" | "N";
    choose_rooms_by: "name" | "tag";
    tags: string[];
    recurrence_choice: string;
    one_off_schedule_choice: string;
    recurring_schedule_choice: string;
    recurring_schedule_interval_period: string;
    recurring_schedule_periodic_period: string;
    recurring_schedule_periodic_period_dow_values: any[];
    recurring_schedule_periodic_period_dom_values: any[];
  }>({
    text: { EN: "", FR: "", IT: "", DE: "" },
    rooms: [],
    type: "A",
    choose_rooms_by: "name",
    tags: [],
    recurrence_choice: "O",
    one_off_schedule_choice: "now",
    recurring_schedule_choice: "INT",
    recurring_schedule_interval_period: "MIN",
    recurring_schedule_periodic_period: "ALL",
    recurring_schedule_periodic_period_dow_values: [],
    recurring_schedule_periodic_period_dom_values: [],
  });
  const [oneOffScheduleDate, setOneOffScheduleDate] = useState<any>({
    startDate: null,
    endDate: null,
  });
  const [oneOffScheduleTime, setOneOffScheduleTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });
  const [periodicScheduleInterval, setPeriodicScheduleInterval] = useState<{
    MIN: number;
    HRS: number;
    DAY: number;
  }>({ MIN: 1, HRS: 1, DAY: 1 });
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const [periodicScheduleIntervalDate, setPeriodicScheduleIntervalDate] =
    useState<any>({
      startDate: null,
      endDate: null,
    });
  const [periodicScheduleIntervalTime, setPeriodicScheduleIntervalTime] =
    useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [periodicSchedulePeriodicTime, setPeriodicSchedulePeriodicTime] =
    useState<{ hour: any; minute: any }>({ hour: "", minute: "" });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { announcements, options } = useAppSelector(
    (state) => state.communication,
  );
  const { rooms } = useAppSelector((state) => state.rooms);
  const language = user === null ? "UK" : user.language;

  // Functions
  function openCreateAnnouncementModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  function selectOrUnselectAllRooms(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        rooms: allAvailableRooms,
      });
    } else {
      setCreateAnnouncementForm({ ...createAnnouncementForm, rooms: [] });
    }
  }

  function selectOrUnselectAllTags(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableTags = roomTags.map((x: any) => x.id);
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        tags: allAvailableTags,
      });
    } else {
      setCreateAnnouncementForm({ ...createAnnouncementForm, tags: [] });
    }
  }

  function updateRoomForAnnouncement(roomId: string) {
    if (createAnnouncementForm.rooms.includes(roomId)) {
      const updatedRooms = createAnnouncementForm.rooms.filter(
        (x) => x !== roomId,
      );
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        rooms: updatedRooms,
      });
    } else {
      const updatedRooms = [...createAnnouncementForm.rooms, roomId];
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        rooms: updatedRooms,
      });
    }
  }

  function updateTagForAnnouncement(tag: string) {
    const existingTags = createAnnouncementForm.tags;
    if (existingTags.includes(tag)) {
      const updatedTags = existingTags.filter((x) => x !== tag);
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        tags: updatedTags,
      });
    } else {
      const updatedTags = [...existingTags, tag];
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        tags: updatedTags,
      });
    }
  }

  async function confirmCreateAnnouncement(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      text,
      rooms,
      type,
      choose_rooms_by: chooseRoomBy,
      tags,
      recurrence_choice: recurrenceChoice,
      one_off_schedule_choice: oneOffScheduleChoice,
      recurring_schedule_choice: recurringScheduleChoice,
      recurring_schedule_interval_period: recurringScheduleIntervalPeriod,
      recurring_schedule_periodic_period: recurringSchedulePeriodicPeriod,
      recurring_schedule_periodic_period_dow_values:
        recurringSchedulePeriodicPeriodDowValues,
      recurring_schedule_periodic_period_dom_values:
        recurringSchedulePeriodicPeriodDomValues,
    } = createAnnouncementForm;

    // Validate announcement text
    if (text[language].length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_text", language), "danger", 5000),
      );
      return;
    }

    // Build and validate rooms array
    let announcementRooms = rooms;
    if (chooseRoomBy === "tag") {
      if (tags.length === 0) {
        dispatch(
          addAlert(generateAlertText("missing_tags", language), "danger", 5000),
        );
        return;
      }
      announcementRooms = [];
      for (let i = 0; i < roomsData.length; i++) {
        const roomTags = roomsData[i].tag;
        const commonTags = roomTags.filter((x: any) => tags.includes(x));
        if (commonTags.length > 0) {
          announcementRooms.push(roomsData[i].id);
        }
      }
    }
    if (announcementRooms.length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 5000),
      );
      return;
    }

    // Validate data and send if recurrence choice is one off
    const now = new Date();
    if (recurrenceChoice === "O") {
      if (oneOffScheduleChoice === "now") {
        const obj = { text, rooms: announcementRooms, type };
        setShowCreateModal(false);
        const res: any = await dispatch(createAnnouncementNow(obj));
        if (res.payload.status === 200) {
          dispatch(
            addAlert(
              generateAlertText("create_announcement", language),
              "success",
              3000,
            ),
          );
          setCreateAnnouncementForm({
            text: { EN: "", FR: "", IT: "", DE: "" },
            rooms: [],
            type: "A",
            choose_rooms_by: "name",
            tags: [],
            recurrence_choice: "O",
            one_off_schedule_choice: "now",
            recurring_schedule_choice: "INT",
            recurring_schedule_interval_period: "MIN",
            recurring_schedule_periodic_period: "ALL",
            recurring_schedule_periodic_period_dow_values: [],
            recurring_schedule_periodic_period_dom_values: [],
          });
        } else {
          dispatch(
            addAlert(
              generateAlertText("create_error", language),
              "danger",
              3000,
            ),
          );
          setShowCreateModal(true);
        }
        return;
      }
      if (oneOffScheduleChoice === "future") {
        if (oneOffScheduleDate.startDate === null) {
          dispatch(
            addAlert(
              generateAlertText("missing_date", language),
              "danger",
              5000,
            ),
          );
          return;
        }
        const { hour, minute } = oneOffScheduleTime;
        if (hour === "" || minute === "") {
          dispatch(
            addAlert(
              generateAlertText("missing_times", language),
              "danger",
              5000,
            ),
          );
          return;
        }
        let scheduledDate = oneOffScheduleDate.startDate;
        const scheduledDateSplit = scheduledDate.split("-");
        const scheduledYear = parseInt(scheduledDateSplit[0], 10);
        const scheduledMonth = parseInt(scheduledDateSplit[1], 10) - 1;
        const scheduledDay = parseInt(scheduledDateSplit[2], 10);
        scheduledDate = new Date(
          scheduledYear,
          scheduledMonth,
          scheduledDay,
          hour,
          minute,
          0,
        );

        if (scheduledDate < now) {
          dispatch(
            addAlert(
              generateAlertText("date_is_past", language),
              "danger",
              5000,
            ),
          );
          return;
        }

        const formattedDatetime = formatDatetimeAsString(scheduledDate);
        const obj = {
          text,
          rooms: announcementRooms,
          datetime: formattedDatetime,
          type,
        };
        setShowCreateModal(false);
        const res: any = await dispatch(createAnnouncementScheduled(obj));
        if (res.payload.status === 200) {
          dispatch(
            addAlert(
              generateAlertText("create_announcement", language),
              "success",
              3000,
            ),
          );
          setCreateAnnouncementForm({
            text: { EN: "", FR: "", IT: "", DE: "" },
            rooms: [],
            type: "A",
            choose_rooms_by: "name",
            tags: [],
            recurrence_choice: "O",
            one_off_schedule_choice: "now",
            recurring_schedule_choice: "INT",
            recurring_schedule_interval_period: "MIN",
            recurring_schedule_periodic_period: "ALL",
            recurring_schedule_periodic_period_dow_values: [],
            recurring_schedule_periodic_period_dom_values: [],
          });
        } else {
          dispatch(
            addAlert(
              generateAlertText("create_error", language),
              "danger",
              3000,
            ),
          );
          setShowCreateModal(true);
        }
        return;
      }
    }

    // Validate data and send if recurrence choice is recurring
    if (recurrenceChoice === "R") {
      if (recurringScheduleChoice === "INT") {
        if (periodicScheduleIntervalDate.startDate === null) {
          dispatch(
            addAlert(
              generateAlertText("missing_date", language),
              "danger",
              5000,
            ),
          );
          return;
        }
        const { hour, minute } = periodicScheduleIntervalTime;
        if (hour === "" || minute === "") {
          dispatch(
            addAlert(
              generateAlertText("missing_times", language),
              "danger",
              5000,
            ),
          );
          return;
        }
        let scheduledDate = periodicScheduleIntervalDate.startDate;
        const scheduledDateSplit = scheduledDate.split("-");
        const scheduledYear = parseInt(scheduledDateSplit[0], 10);
        const scheduledMonth = parseInt(scheduledDateSplit[1], 10);
        const scheduledDay = parseInt(scheduledDateSplit[2], 10);
        scheduledDate = new Date(
          scheduledYear,
          scheduledMonth,
          scheduledDay,
          hour,
          minute,
          0,
        );

        if (recurringScheduleIntervalPeriod === "MIN") {
          if (
            periodicScheduleInterval.MIN < 1 ||
            periodicScheduleInterval.MIN > 59
          ) {
            dispatch(
              addAlert(
                generateAlertText("invalid_minutes", language),
                "danger",
                5000,
              ),
            );
            return;
          }
        }
        if (recurringScheduleIntervalPeriod === "HRS") {
          if (
            periodicScheduleInterval.HRS < 1 ||
            periodicScheduleInterval.HRS > 23
          ) {
            dispatch(
              addAlert(
                generateAlertText("invalid_hours", language),
                "danger",
                5000,
              ),
            );
            return;
          }
        }
        if (recurringScheduleIntervalPeriod === "DAY") {
          if (
            periodicScheduleInterval.DAY < 1 ||
            periodicScheduleInterval.DAY > 31
          ) {
            dispatch(
              addAlert(
                generateAlertText("invalid_days", language),
                "danger",
                5000,
              ),
            );
            return;
          }
        }

        const formattedDatetime = formatDatetimeAsString(scheduledDate);
        const obj = {
          text,
          rooms: announcementRooms,
          type,
          start_time: formattedDatetime,
          schedule_type: "INT",
          schedule_params: {
            every:
              recurringScheduleIntervalPeriod === "MIN"
                ? periodicScheduleInterval.MIN
                : recurringScheduleIntervalPeriod === "HRS"
                ? periodicScheduleInterval.HRS
                : periodicScheduleInterval.DAY,
            period: recurringScheduleIntervalPeriod,
          },
        };
        setShowCreateModal(false);
        const res: any = await dispatch(
          createAnnouncementRecurringInterval(obj),
        );
        if (res.payload.status === 200) {
          dispatch(
            addAlert(
              generateAlertText("create_announcement", language),
              "success",
              3000,
            ),
          );
          setCreateAnnouncementForm({
            text: { EN: "", FR: "", IT: "", DE: "" },
            rooms: [],
            type: "A",
            choose_rooms_by: "name",
            tags: [],
            recurrence_choice: "O",
            one_off_schedule_choice: "now",
            recurring_schedule_choice: "INT",
            recurring_schedule_interval_period: "MIN",
            recurring_schedule_periodic_period: "ALL",
            recurring_schedule_periodic_period_dow_values: [],
            recurring_schedule_periodic_period_dom_values: [],
          });
        } else {
          dispatch(
            addAlert(
              generateAlertText("create_error", language),
              "danger",
              3000,
            ),
          );
          setShowCreateModal(true);
        }
        return;
      }

      if (recurringScheduleChoice === "PRD") {
        const { hour, minute } = periodicSchedulePeriodicTime;
        if (hour === "" || minute === "") {
          dispatch(
            addAlert(
              generateAlertText("missing_times", language),
              "danger",
              5000,
            ),
          );
          return;
        }

        if (recurringSchedulePeriodicPeriod === "ALL") {
          const obj = {
            text,
            rooms: announcementRooms,
            type,
            schedule_type: "PRD",
            schedule_params: {
              hour: parseInt(hour, 10),
              minute: parseInt(minute, 10),
              period: "ALL",
            },
          };
          setShowCreateModal(false);
          const res: any = await dispatch(
            createAnnouncementRecurringPeriod(obj),
          );
          if (res.payload.status === 200) {
            dispatch(
              addAlert(
                generateAlertText("create_announcement", language),
                "success",
                3000,
              ),
            );
            setCreateAnnouncementForm({
              text: { EN: "", FR: "", IT: "", DE: "" },
              rooms: [],
              type: "A",
              choose_rooms_by: "name",
              tags: [],
              recurrence_choice: "O",
              one_off_schedule_choice: "now",
              recurring_schedule_choice: "INT",
              recurring_schedule_interval_period: "MIN",
              recurring_schedule_periodic_period: "ALL",
              recurring_schedule_periodic_period_dow_values: [],
              recurring_schedule_periodic_period_dom_values: [],
            });
          } else {
            dispatch(
              addAlert(
                generateAlertText("create_error", language),
                "danger",
                3000,
              ),
            );
            setShowCreateModal(true);
          }
          return;
        }

        if (recurringSchedulePeriodicPeriod === "DOW") {
          if (recurringSchedulePeriodicPeriodDowValues.length === 0) {
            dispatch(
              addAlert(
                generateAlertText("missing_dow", language),
                "danger",
                5000,
              ),
            );
            return;
          }
          const obj = {
            text,
            rooms: announcementRooms,
            type,
            schedule_type: "PRD",
            schedule_params: {
              hour: parseInt(hour, 10),
              minute: parseInt(minute, 10),
              period: "DOW",
              period_value: recurringSchedulePeriodicPeriodDowValues,
            },
          };
          setShowCreateModal(false);
          const res: any = await dispatch(
            createAnnouncementRecurringPeriod(obj),
          );
          if (res.payload.status === 200) {
            dispatch(
              addAlert(
                generateAlertText("create_announcement", language),
                "success",
                3000,
              ),
            );
            setCreateAnnouncementForm({
              text: { EN: "", FR: "", IT: "", DE: "" },
              rooms: [],
              type: "A",
              choose_rooms_by: "name",
              tags: [],
              recurrence_choice: "O",
              one_off_schedule_choice: "now",
              recurring_schedule_choice: "INT",
              recurring_schedule_interval_period: "MIN",
              recurring_schedule_periodic_period: "ALL",
              recurring_schedule_periodic_period_dow_values: [],
              recurring_schedule_periodic_period_dom_values: [],
            });
          } else {
            dispatch(
              addAlert(
                generateAlertText("create_error", language),
                "danger",
                3000,
              ),
            );
            setShowCreateModal(true);
          }
          return;
        }

        if (recurringSchedulePeriodicPeriod === "DOM") {
          if (recurringSchedulePeriodicPeriodDomValues.length === 0) {
            dispatch(
              addAlert(
                generateAlertText("missing_dom", language),
                "danger",
                5000,
              ),
            );
            return;
          }
          const obj = {
            text,
            rooms: announcementRooms,
            type,
            schedule_type: "PRD",
            schedule_params: {
              hour: parseInt(hour, 10),
              minute: parseInt(minute, 10),
              period: "DOM",
              period_value: recurringSchedulePeriodicPeriodDomValues,
            },
          };
          setShowCreateModal(false);
          const res: any = await dispatch(
            createAnnouncementRecurringPeriod(obj),
          );
          if (res.payload.status === 200) {
            dispatch(
              addAlert(
                generateAlertText("create_announcement", language),
                "success",
                3000,
              ),
            );
            setCreateAnnouncementForm({
              text: { EN: "", FR: "", IT: "", DE: "" },
              rooms: [],
              type: "A",
              choose_rooms_by: "name",
              tags: [],
              recurrence_choice: "O",
              one_off_schedule_choice: "now",
              recurring_schedule_choice: "INT",
              recurring_schedule_interval_period: "MIN",
              recurring_schedule_periodic_period: "ALL",
              recurring_schedule_periodic_period_dow_values: [],
              recurring_schedule_periodic_period_dom_values: [],
            });
          } else {
            dispatch(
              addAlert(
                generateAlertText("create_error", language),
                "danger",
                3000,
              ),
            );
            setShowCreateModal(true);
          }
          return;
        }
      }
    }
  }

  function updateDowForAnnouncement(dow: any) {
    if (
      createAnnouncementForm.recurring_schedule_periodic_period_dow_values.includes(
        dow,
      )
    ) {
      const updatedDow =
        createAnnouncementForm.recurring_schedule_periodic_period_dow_values.filter(
          (x) => x !== dow,
        );
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        recurring_schedule_periodic_period_dow_values: updatedDow,
      });
    } else {
      const updatedDow = [
        ...createAnnouncementForm.recurring_schedule_periodic_period_dow_values,
        dow,
      ];
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        recurring_schedule_periodic_period_dow_values: updatedDow,
      });
    }
  }

  function updateDomForAnnouncement(dom: any) {
    if (
      createAnnouncementForm.recurring_schedule_periodic_period_dom_values.includes(
        dom,
      )
    ) {
      const updatedDom =
        createAnnouncementForm.recurring_schedule_periodic_period_dom_values.filter(
          (x) => x !== dom,
        );
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        recurring_schedule_periodic_period_dom_values: updatedDom,
      });
    } else {
      const updatedDom = [
        ...createAnnouncementForm.recurring_schedule_periodic_period_dom_values,
        dom,
      ];
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        recurring_schedule_periodic_period_dom_values: updatedDom,
      });
    }
  }

  function selectOrUnselectAllDow(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableDow = daysOfTheWeek.map((x) => x[0]);
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        recurring_schedule_periodic_period_dow_values: allAvailableDow,
      });
    } else {
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        recurring_schedule_periodic_period_dow_values: [],
      });
    }
  }

  function selectOrUnselectAllDom(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableDom = daysOfTheMonth.map((x) => x[0]);
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        recurring_schedule_periodic_period_dom_values: allAvailableDom,
      });
    } else {
      setCreateAnnouncementForm({
        ...createAnnouncementForm,
        recurring_schedule_periodic_period_dom_values: [],
      });
    }
  }

  function getNumColumnsInGrid() {
    if (searchType === "O") return 1;
    return 3;
  }

  async function deleteSelectedAnnouncements(e: any, args: any[]) {
    e.preventDefault();
    const [announcementIds] = args;
    const res: any = await dispatch(deleteAnnouncements({ announcementIds }));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_announcement", language),
          "success",
          3000,
        ),
      );
      setSelectedAnnouncements([]);
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
    }
  }

  async function enableSelectedAnnouncements(e: any, args: any[]) {
    e.preventDefault();
    const [announcementIds] = args;
    const res: any = await dispatch(
      updatePeriodicAnnouncementStatus({ announcementIds, status: "E" }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("enable_announcements", language),
          "success",
          3000,
        ),
      );
      setSelectedAnnouncements([]);
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
    }
  }

  async function disableSelectedAnnouncements(e: any, args: any[]) {
    e.preventDefault();
    const [announcementIds] = args;
    const res: any = await dispatch(
      updatePeriodicAnnouncementStatus({ announcementIds, status: "D" }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("disable_announcements", language),
          "success",
          3000,
        ),
      );
      setSelectedAnnouncements([]);
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
    }
  }

  function selectAllAnnouncements(announcementId: any) {
    const currentAnnouncementIds = announcementsData.map((ann) => ann.id);

    // If all are selected, unselect all
    if (
      selectedAnnouncements.length > 0 &&
      selectedAnnouncements.length === currentAnnouncementIds.length
    ) {
      setSelectedAnnouncements([]);
      return;
    }

    // Otherwise, select all
    setSelectedAnnouncements(currentAnnouncementIds);
  }

  function selectAnnouncement(announcementId: string) {
    const updatedSelectedAnnouncements = [...selectedAnnouncements];
    if (!updatedSelectedAnnouncements.includes(announcementId)) {
      updatedSelectedAnnouncements.push(announcementId);
    } else {
      updatedSelectedAnnouncements.splice(
        updatedSelectedAnnouncements.indexOf(announcementId),
        1,
      );
    }
    setSelectedAnnouncements(updatedSelectedAnnouncements);
  }

  function getDeleteAnnouncementsText(numAnnouncements: number) {
    if (language === "EN") {
      if (numAnnouncements === 1) return "Delete announcement";
      return "Delete announcements";
    }
    if (language === "FR") {
      if (numAnnouncements === 1) return "Supprimer l'annonce";
      return "Supprimer les annonces";
    }
    if (language === "IT") {
      if (numAnnouncements === 1) return "Elimina annuncio";
      return "Elimina annunci";
    }
    if (numAnnouncements === 1) return "Delete announcement";
    return "Delete announcements";
  }

  // Format rooms data and filter by query param tag
  const roomsData = rooms.data === null ? [] : rooms.data;
  const announcementsData =
    announcements.data === null ? [] : announcements.data;
  const announcementTypes =
    options.announcements === undefined
      ? []
      : Object.keys(options.announcements).map((key) => [
          key,
          options.announcements[key][language],
        ]);
  const chooseRoomsBy = [
    ["name", translateText("name", language)],
    ["tag", translateText("tag_single", language)],
  ];
  const recurrenceChoices = [
    ["O", translateText("one_off", language)],
    ["R", translateText("recurring", language)],
  ];
  const oneOffScheduleChoices = [
    ["now", translateText("now", language)],
    ["future", translateText("future", language)],
  ];
  const recurringScheduleChoices =
    options.schedule_type === undefined
      ? []
      : Object.keys(options.schedule_type.periodic).map((key) => [
          key,
          options.schedule_type.periodic[key][language],
        ]);
  const recurringScheduleIntervalChoices =
    options.schedule_type === undefined
      ? []
      : Object.keys(options.periodic_choices.interval).map((key) => [
          key,
          options.periodic_choices.interval[key][language],
        ]);
  const recurringSchedulePeriodicChoices =
    options.schedule_type === undefined
      ? []
      : Object.keys(options.periodic_choices.periodic).map((key) => [
          key,
          options.periodic_choices.periodic[key][language],
        ]);
  const daysOfTheWeek = daysOfWeek(language);
  const daysOfTheMonth = daysOfMonth();
  const announcementFilterTypes = recurrenceChoices.map((x) => ({
    value: x[0],
    label: x[1],
  }));

  // Create tags object for filters
  let roomTags: any = [];
  for (let i = 0; i < roomsData.length; i++) {
    const candidateTags = roomsData[i].tag;
    for (let j = 0; j < candidateTags.length; j++) {
      if (!roomTags.includes(candidateTags[j])) {
        roomTags.push(candidateTags[j]);
      }
    }
  }
  roomTags.sort();
  roomTags = roomTags.map((x: any) => ({ id: x, label: x }));

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("announcements_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateAnnouncementModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_announcement", language)}
              </Button>
            }
          />
        }
      />

      {/* Create announcement modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_announcement", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateAnnouncement}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_announcement", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Text */}
              <TextInputMultiLanguage
                id="text"
                type="text"
                label={translateText("text", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createAnnouncementForm}
                stateName="text"
                onChange={setCreateAnnouncementForm}
                defaultLanguage={language}
                languageChoices={["EN", "FR", "IT", "DE"]}
              />

              {/* Announcement type */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("type", language)}
                showLabel={true}
                required={true}
                state={createAnnouncementForm}
                stateName="type"
                onChange={setCreateAnnouncementForm}
                defaultValue="O"
              >
                {announcementTypes.map((type) => (
                  <option key={type[0]} value={type[0]}>
                    {type[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Choose rooms by */}
              <Dropdown
                id="choose_rooms_by"
                className="col-span-full"
                label={translateText("choose_rooms_by", language)}
                small={translateText("choose_rooms_by_subtext", language)}
                showLabel={true}
                required={true}
                state={createAnnouncementForm}
                stateName="choose_rooms_by"
                onChange={setCreateAnnouncementForm}
                defaultValue="name"
              >
                {chooseRoomsBy.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Rooms */}
              {createAnnouncementForm.choose_rooms_by === "name" ? (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("rooms", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText("rooms_template_subtext", language)}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllRooms}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomsData.map((room: any) => (
                      <Checkbox
                        key={room.id}
                        id={room.id}
                        onChange={updateRoomForAnnouncement}
                        checked={createAnnouncementForm.rooms.includes(room.id)}
                        label={room.name}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("tag", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText(
                        "select_tags_for_rooms_announcement",
                        language,
                      )}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllTags}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomTags.map((tag: any) => (
                      <Checkbox
                        key={tag.id}
                        id={tag.id}
                        onChange={updateTagForAnnouncement}
                        checked={createAnnouncementForm.tags.includes(tag.id)}
                        label={tag.label}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Recurrence choice */}
              <HorizontalRadio
                label={translateText("recurrence", language)}
                small={translateText(
                  "recurrence_announcement_subtext",
                  language,
                )}
              >
                <>
                  {recurrenceChoices.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="recurrence_choice"
                        type="radio"
                        defaultChecked={
                          createAnnouncementForm.recurrence_choice === choice[0]
                        }
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setCreateAnnouncementForm({
                            ...createAnnouncementForm,
                            recurrence_choice: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {/* One off recurrence schedule */}
              {createAnnouncementForm.recurrence_choice === "O" && (
                <>
                  {/* Schedule choice */}
                  <HorizontalRadio
                    label={translateText("schedule", language)}
                    small={translateText(
                      "schedule_announcement_subtext",
                      language,
                    )}
                  >
                    <>
                      {oneOffScheduleChoices.map((choice) => (
                        <div key={choice[0]} className="flex items-center">
                          <input
                            id={choice[0]}
                            name="one_off_schedule_choice"
                            type="radio"
                            defaultChecked={
                              createAnnouncementForm.one_off_schedule_choice ===
                              choice[0]
                            }
                            className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                            value={choice[0]}
                            onChange={(e) =>
                              setCreateAnnouncementForm({
                                ...createAnnouncementForm,
                                one_off_schedule_choice: e.target.value,
                              })
                            }
                          />
                          <label
                            htmlFor={choice[0]}
                            className="ml-3 block text-sm font-medium leading-6 text-black"
                          >
                            {choice[1]}
                          </label>
                        </div>
                      ))}
                    </>
                  </HorizontalRadio>

                  {/* Future time picker */}
                  {createAnnouncementForm.one_off_schedule_choice ===
                    "future" && (
                    <DatetimeSelector
                      label=""
                      showLabel={false}
                      dateState={oneOffScheduleDate}
                      dateHandler={setOneOffScheduleDate}
                      timeState={oneOffScheduleTime}
                      timeHandler={setOneOffScheduleTime}
                      language={language}
                    />
                  )}
                </>
              )}

              {/* Recurring recurrence schedule */}
              {createAnnouncementForm.recurrence_choice === "R" && (
                <>
                  {/* Recurring schedule choice */}
                  <HorizontalRadio
                    label={translateText("recurring_schedule", language)}
                    small={translateText(
                      "recurring_schedule_announcement_subtext",
                      language,
                    )}
                  >
                    <>
                      {recurringScheduleChoices.map((choice) => (
                        <div key={choice[0]} className="flex items-center">
                          <input
                            id={choice[0]}
                            name="one_off_schedule_choice"
                            type="radio"
                            defaultChecked={
                              createAnnouncementForm.recurring_schedule_choice ===
                              choice[0]
                            }
                            className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                            value={choice[0]}
                            onChange={(e) =>
                              setCreateAnnouncementForm({
                                ...createAnnouncementForm,
                                recurring_schedule_choice: e.target.value,
                              })
                            }
                          />
                          <label
                            htmlFor={choice[0]}
                            className="ml-3 block text-sm font-medium leading-6 text-black"
                          >
                            {choice[1]}
                          </label>
                        </div>
                      ))}
                    </>
                  </HorizontalRadio>

                  {/* If choice is an Interval schedule */}
                  {createAnnouncementForm.recurring_schedule_choice ===
                    "INT" && (
                    <>
                      <div className="grid grid-cols-2 gap-x-2">
                        {createAnnouncementForm.recurring_schedule_interval_period ===
                          "MIN" && (
                          <IntegerInput
                            id="recurring_periodic_schedule_choice_minutes"
                            min={1}
                            max={60}
                            label={translateText("every", language)}
                            className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                            required={true}
                            showLabel={true}
                            state={periodicScheduleInterval}
                            stateName="MIN"
                            onChange={setPeriodicScheduleInterval}
                          />
                        )}

                        {createAnnouncementForm.recurring_schedule_interval_period ===
                          "HRS" && (
                          <IntegerInput
                            id="recurring_periodic_schedule_choice_hours"
                            min={1}
                            max={24}
                            label={translateText("every", language)}
                            className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                            required={true}
                            showLabel={true}
                            state={periodicScheduleInterval}
                            stateName="HRS"
                            onChange={setPeriodicScheduleInterval}
                          />
                        )}

                        {createAnnouncementForm.recurring_schedule_interval_period ===
                          "DAY" && (
                          <IntegerInput
                            id="recurring_periodic_schedule_choice_days"
                            min={1}
                            max={31}
                            label={translateText("every", language)}
                            className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                            required={true}
                            showLabel={true}
                            state={periodicScheduleInterval}
                            stateName="DAY"
                            onChange={setPeriodicScheduleInterval}
                          />
                        )}

                        {/* Interval type selection */}
                        <Dropdown
                          id="recurring_schedule_interval_period"
                          className="col-span-full mt-8"
                          label=""
                          showLabel={false}
                          required={true}
                          state={createAnnouncementForm}
                          stateName="recurring_schedule_interval_period"
                          onChange={setCreateAnnouncementForm}
                          defaultValue="MIN"
                        >
                          {recurringScheduleIntervalChoices.map(
                            // (choice) => {
                            //   if (choice[0] === createAnnouncementForm.recurring_schedule_interval_period) {
                            //     return (
                            //       <option key={choice[0]} value={choice[0]} selected>
                            //         {choice[1]}
                            //       </option>
                            //     )
                            //   } else {
                            //     return (
                            //       <option key={choice[0]} value={choice[0]}>
                            //         {choice[1]}
                            //       </option>
                            //     );
                            //   }
                            // }
                            (choice) => (
                              <option key={choice[0]} value={choice[0]}>
                                {choice[1]}
                              </option>
                            ),
                          )}
                        </Dropdown>
                      </div>

                      {/* Starting from */}
                      <DatetimeSelector
                        label={translateText("start_from", language)}
                        showLabel={true}
                        dateState={periodicScheduleIntervalDate}
                        dateHandler={setPeriodicScheduleIntervalDate}
                        timeState={periodicScheduleIntervalTime}
                        timeHandler={setPeriodicScheduleIntervalTime}
                        language={language}
                      />
                    </>
                  )}

                  {/* If choice is a Periodic schedule */}
                  {createAnnouncementForm.recurring_schedule_choice ===
                    "PRD" && (
                    <>
                      {/* Periodic period choice */}
                      <Dropdown
                        id="recurring_schedule_periodic_period"
                        className="col-span-full"
                        label={translateText("period", language)}
                        showLabel={true}
                        required={true}
                        state={createAnnouncementForm}
                        stateName="recurring_schedule_periodic_period"
                        onChange={setCreateAnnouncementForm}
                        defaultValue="ALL"
                      >
                        {recurringSchedulePeriodicChoices.map(
                          // (choice) => {
                          //   if (choice[0] === createAnnouncementForm.recurring_schedule_periodic_period) {
                          //     return (
                          //       <option key={choice[0]} value={choice[0]} selected>
                          //         {choice[1]}
                          //       </option>
                          //     )
                          //   } else {
                          //     return (
                          //       <option key={choice[0]} value={choice[0]}>
                          //         {choice[1]}
                          //       </option>
                          //     );
                          //   }
                          (choice) => (
                            <option key={choice[0]} value={choice[0]}>
                              {choice[1]}
                            </option>
                          ),
                          // }
                        )}
                      </Dropdown>

                      {/* If periodic period choice is Specific days of the week */}
                      {createAnnouncementForm.recurring_schedule_periodic_period ===
                        "DOW" && (
                        <>
                          {/* Select/unselect all buttons */}
                          <SelectOrUnselectAll
                            handler={selectOrUnselectAllDow}
                            language={language}
                          />
                          <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                            {daysOfTheWeek.map((dow: any) => (
                              <Checkbox
                                key={dow[0]}
                                id={dow[0]}
                                onChange={updateDowForAnnouncement}
                                checked={createAnnouncementForm.recurring_schedule_periodic_period_dow_values.includes(
                                  dow[0],
                                )}
                                label={dow[1]}
                              />
                            ))}
                          </div>
                        </>
                      )}

                      {/* If periodic period choice is Specific days of the month */}
                      {createAnnouncementForm.recurring_schedule_periodic_period ===
                        "DOM" && (
                        <>
                          {/* Select/unselect all buttons */}
                          <SelectOrUnselectAll
                            handler={selectOrUnselectAllDom}
                            language={language}
                          />
                          <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                            {daysOfTheMonth.map((dom: any) => (
                              <Checkbox
                                key={dom[0]}
                                id={dom[0]}
                                onChange={updateDomForAnnouncement}
                                checked={createAnnouncementForm.recurring_schedule_periodic_period_dom_values.includes(
                                  dom[0],
                                )}
                                label={dom[1]}
                              />
                            ))}
                          </div>
                        </>
                      )}

                      {/* Choose start time period */}
                      <TimeSelector
                        label={translateText("at_time", language)}
                        showLabel={true}
                        timeState={periodicSchedulePeriodicTime}
                        timeHandler={setPeriodicSchedulePeriodicTime}
                        language={language}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {announcements.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "type",
                  name: translateText("type", language),
                  options: announcementFilterTypes,
                },
              ]}
              queryParams={[["type", searchType]]}
              handlers={{
                type: {
                  state: type,
                  stateName: "value",
                  onChange: setType,
                },
              }}
              loading={announcements.loading}
              baseUrl="/communication/announcements"
              showDateRange={false}
              showFilters={true}
              showSearchBar={false}
            />
            {announcementsData.length > 0 ? (
              <>
                {/* Table actions */}
                {selectedAnnouncements.length > 0 && (
                  <div className="mx-1">
                    <p className="text-sm text-granite italic">{`${
                      selectedAnnouncements.length
                    } selected announcement${
                      selectedAnnouncements.length === 1 ? "" : "s"
                    }`}</p>
                    <div
                      className={`mt-2 mb-4 grid grid-cols-1 md:grid-cols-${getNumColumnsInGrid()} space-y-2 md:space-y-0 gap-x-2`}
                    >
                      {/* Action buttons for one off announcements */}
                      {searchType === "O" ? (
                        <>
                          {/* Delete one off announcements */}
                          <Button
                            type="button"
                            className={`${getButtonStyle("solid", "danger")}`}
                            onClick={deleteSelectedAnnouncements}
                            onClickArgs={[selectedAnnouncements]}
                          >
                            <TrashIcon
                              className="w-5 h-5 mr-2"
                              aria-hidden="true"
                            />
                            {getDeleteAnnouncementsText(
                              selectedAnnouncements.length,
                            )}
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* Delete recurring announcements */}
                          <Button
                            type="button"
                            className={`${getButtonStyle("solid", "danger")}`}
                            onClick={deleteSelectedAnnouncements}
                            onClickArgs={[selectedAnnouncements]}
                          >
                            <TrashIcon
                              className="w-5 h-5 mr-2"
                              aria-hidden="true"
                            />
                            {getDeleteAnnouncementsText(
                              selectedAnnouncements.length,
                            )}
                          </Button>

                          {/* Enable recurring announcements */}
                          <Button
                            type="button"
                            className={`${getButtonStyle("solid", "success")}`}
                            onClick={enableSelectedAnnouncements}
                            onClickArgs={[selectedAnnouncements]}
                          >
                            <PlayIcon
                              className="w-5 h-5 mr-2"
                              aria-hidden="true"
                            />
                            {translateText("enable", language)}
                          </Button>

                          {/* Disable recurring announcements */}
                          <Button
                            type="button"
                            className={`${getButtonStyle("solid", "warning")}`}
                            onClick={disableSelectedAnnouncements}
                            onClickArgs={[selectedAnnouncements]}
                          >
                            <PauseIcon
                              className="w-5 h-5 mr-2"
                              aria-hidden="true"
                            />
                            {translateText("disable", language)}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Announcements table */}
                <Table
                  header={
                    <tr>
                      <th className="px-2 py-3">
                        <Checkbox
                          id="select-all"
                          onChange={selectAllAnnouncements}
                          checked={
                            selectedAnnouncements.length > 0 &&
                            selectedAnnouncements.length ===
                              announcementsData.length
                          }
                        />
                      </th>
                      <th className="px-2 py-3">
                        {translateText("start_from", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("schedule_type", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("scheduled_for", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("text", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("last_updated", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("destination", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("created_by", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("type", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("status", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("run_count", language)}
                      </th>
                    </tr>
                  }
                  body={announcementsData.map((announcement) => (
                    <tr
                      key={announcement.id}
                      className={`${
                        selectedAnnouncements.includes(announcement.id) &&
                        "bg-light/20"
                      } hover:bg-light/20`}
                      onClick={() => selectAnnouncement(announcement.id)}
                    >
                      <td className="border-r border-light/50 px-2 py-3">
                        <Checkbox
                          id={announcement.id}
                          onChange={selectAnnouncement}
                          checked={selectedAnnouncements.includes(
                            announcement.id,
                          )}
                        />
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {announcement.starting_at}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        <Badge
                          text={announcement.schedule_type[1]}
                          bgColour={getColour(announcement.schedule_type[0])}
                          textColour={`${getColour(
                            announcement.schedule_type[0],
                          )}-dark`}
                          ringColour={`${getColour(
                            announcement.schedule_type[0],
                          )}-dark`}
                        />
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {announcement.scheduled_for}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {announcement.text[language]}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {announcement.last_updated}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {announcement.rooms}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {announcement.sent_by}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        <Badge
                          text={announcement.type[1]}
                          bgColour={getColour(announcement.type[0])}
                          textColour={`${getColour(announcement.type[0])}-dark`}
                          ringColour={`${getColour(announcement.type[0])}-dark`}
                        />
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        <Badge
                          text={announcement.status[1]}
                          bgColour={getColour(announcement.status[0])}
                          textColour={`${getColour(
                            announcement.status[0],
                          )}-dark`}
                          ringColour={`${getColour(
                            announcement.status[0],
                          )}-dark`}
                        />
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap">
                        {announcement.total_run_count}
                      </td>
                    </tr>
                  ))}
                  paginationHasPrevious={announcements.pagination.has_previous}
                  paginationHasNext={announcements.pagination.has_next}
                  paginationCurrentPage={announcements.pagination.current_page}
                  paginationTotalPages={announcements.pagination.total_pages}
                  paginationTotalObjects={
                    announcements.pagination.total_objects
                  }
                  paginationQueryParams={["type"]}
                />
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_announcements", language)}
                subtext={translateText("no_announcements_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
